import axios from "axios";
import { Response } from "@/shared/models/Response";
import { AvailabilityModel } from "../models/AvailabilityModel";
import { AvailabilityFilter } from "../models/AvailabilityFilter";
import { EventDataModel } from "@/modules/Profile/components/Appointment/models/EventDataModel";
import { AvailabilityFilterDetailed } from "../models/AvailabilityFilterDetailed.model";
import { EventPatients } from "../models/EventPatients.model";
import { EventRange } from "../models/EventRange.model";
import { List } from "@/shared/components/GenericSearchBar/model/List";

export class AvailabilityService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${process.env.VUE_APP_SCHEDULING_API}/availability`;
  }

  async getAvailabityByFilters(
    filterQuery: AvailabilityFilter
  ): Promise<Response<AvailabilityModel[]>> {
    const serviceURL = `${this.baseUrl}`;
    const response = await axios.get(serviceURL, {
      params: filterQuery
    });
    return response.data;
  }

  async getAvailabilityById(
    itemId: string
  ): Promise<Response<AvailabilityModel>> {
    const serviceURL = `${this.baseUrl}/${itemId}`;
    const response = await axios.get(serviceURL);
    return response.data;
  }

  async createAvailability(
    availability: AvailabilityModel
  ): Promise<Response<string>> {
    const serviceURL = `${this.baseUrl}/`;
    const response = await axios.post(serviceURL, availability);
    return response.data;
  }

  async getCampusAvailables(
    serviceId: string
  ): Promise<Response<AvailabilityModel[]>> {
    const serviceURL = `${this.baseUrl}/campus`;
    const response = await axios.get(serviceURL, {
      params: { service: serviceId }
    });
    return response.data;
  }

  async getCampusByAvailablityTemplateId(
    availibilityTemps: string[]
  ): Promise<Response<AvailabilityModel[]>> {
    const serviceURL = `${this.baseUrl}/campus-by-avalibility-temp`;
    const response = await axios.get(serviceURL, {
      params: { availibilityTemps: availibilityTemps }
    });
    return response.data;
  }

  async availableEvents(
    serviceTemplateId: string,
    campusId: string,
    date: number,
    patientId: string
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  ): Promise<Response<any[]>> {
    const serviceURL = `${this.baseUrl}/availableEvents`;
    const response = await axios.get(serviceURL, {
      params: {
        serviceTemplatesKey: serviceTemplateId,
        campusKey: campusId,
        date: date,
        patientId
      }
    });
    return response.data;
  }

  async moreAvailableEvents(
    serviceTemplateId: string,
    campusId: string,
    date: number
    /* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
  ): Promise<Response<any[]>> {
    const serviceURL = `${this.baseUrl}/moreAvailableEvents`;
    const response = await axios.get(serviceURL, {
      params: {
        serviceTemplatesKey: serviceTemplateId,
        campusKey: campusId,
        date: date
      }
    });
    return response.data;
  }

  async getAvailabilityDataEditById(
    itemId: string
  ): Promise<Response<AvailabilityModel>> {
    const serviceURL = `${this.baseUrl}/edit/${itemId}`;
    const response = await axios.get(serviceURL);
    return response.data;
  }

  async updateAvailabilityByIdWithOption(
    id: string,
    availability: AvailabilityModel,
    option: string
  ): Promise<Response<string | void>> {
    const serviceUrl = `${this.baseUrl}/${id}`;
    const response = await axios.put(serviceUrl, {
      data: { availability, option }
    });
    return response.data;
  }

  async deleteAvailabilityByIdWithOption(
    id: string,
    option: string
  ): Promise<Response<string | void>> {
    const serviceURL = `${this.baseUrl}/${id}`;
    const response = await axios.delete(serviceURL, {
      data: { option }
    });
    return response.data;
  }

  async unlockEvents(
    eventList: EventDataModel[],
    patientId: string
  ): Promise<Response<string | void>> {
    const serviceURL = `${this.baseUrl}/unlock-events`;
    const response = await axios.put(serviceURL, { eventList, patientId });
    return response.data;
  }

  async getAvailabilityByDetailedFilter(
    filters: AvailabilityFilterDetailed
  ): Promise<Response<Array<AvailabilityModel> | void>> {
    const serviceUrl = `${this.baseUrl}/detailed-filter`;
    const response = await axios.get(serviceUrl, {
      params: { ...filters }
    });
    return response.data;
  }

  async getRangeOfEventsData(
    availabilityKey: string,
    start: number,
    end: number
  ): Promise<Response<EventPatients>> {
    const serviceUrl = `${this.baseUrl}/patients-info`;
    const response = await axios.get(serviceUrl, {
      params: {
        id: availabilityKey,
        start,
        end
      }
    });
    return response.data;
  }

  async updateAvailabilityEvent(
    id: string,
    status: string,
    event: EventRange
  ): Promise<Response<string | void>> {
    const serviceUrl = `${this.baseUrl}/${id}/events`;
    const response = await axios.put(serviceUrl, {
      status,
      event
    });
    return response.data;
  }

  async deleteAvailabilityEvent(
    id: string,
    range: string
  ): Promise<Response<string | void>> {
    const serviceUrl = `${this.baseUrl}/${id}/events/${range}`;
    const response = await axios.delete(serviceUrl);
    return response.data;
  }

  async getAvailabilityNames(name?: string): Promise<Response<List[] | void>> {
    const serviceUrl = `${this.baseUrl}/availability-names`;
    const response = await axios.get(serviceUrl, { params: { name: name } });
    return response.data;
  }
}
